import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function Home() {
  const organizationSchema = generateOrganizationSchema();
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [organizationSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'InteCare - Safe Non-Emergency Medical Transportation',
    description: 'InteCare provides safe, comfortable, and reliable non-emergency medical transportation services in Westminster CA and Orange County. Wheelchair, ambulatory, and gurney transport available 24/7 with Integrity and Care.',
    keywords: 'medical transportation Westminster CA, NEMT Orange County, wheelchair transport, ambulatory transport, gurney transport, hospital discharge, non-emergency medical transportation, medical transport services',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-start justify-center bg-cover bg-center pt-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/0828340c1047b151064f961b79741f38.png')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-5 leading-tight">
              Safe, Comfortable, and Reliable Non-Emergency Medical Transportation
            </h1>
            <p className="text-lg md:text-xl mb-8 text-gray-200">
              Serving you with Integrity and Care
            </p>
            <p className="text-base md:text-lg mb-20 text-gray-200">
              Whether you need wheelchair, ambulatory, or gurney transport, InteCare is here to get you where you need to go — safely, on time, and with compassionate care.
            </p>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose InteCare?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're committed to providing the highest quality non-emergency medical transportation with Integrity and Care, focusing on safety, reliability, and compassionate service.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Safety First</h3>
              <p className="text-gray-600">
                All vehicles equipped with safety features and our drivers are professionally trained and certified.
              </p>
            </div>
            
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">On-Time Guarantee</h3>
              <p className="text-gray-600">
                We understand the importance of medical appointments and guarantee punctual service every time.
              </p>
            </div>
            
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Professional Drivers</h3>
              <p className="text-gray-600">
                Compassionate, trained professionals who understand the unique needs of medical transport patients.
              </p>
            </div>
            
            <div className="text-center p-6 bg-orange-50 rounded-xl">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Affordable Rates</h3>
              <p className="text-gray-600">
                Competitive pricing with transparent rates and insurance billing assistance available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Getting reliable medical transportation is easy with InteCare
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Book a Ride</h3>
              <p className="text-gray-600 text-lg">
                Schedule your transportation online or call us. Provide your pickup location, destination, and any special requirements.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Safe Pickup</h3>
              <p className="text-gray-600 text-lg">
                Our professional driver arrives on time with the appropriate vehicle and assists you safely into the transport.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Comfortable Arrival</h3>
              <p className="text-gray-600 text-lg">
                Arrive at your appointment comfortably and on time, with assistance getting to your destination if needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Transportation Services
            </h2>
            <p className="text-xl text-gray-600">
              Specialized medical transportation for every need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border">
              <img 
                src="https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/8ae9bc4b0ff351385c71ce473c35c18d.png"
                alt="Wheelchair Transportation"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Wheelchair Transportation</h3>
                <p className="text-gray-600">
                  Fully accessible vehicles with wheelchair lifts and secure tie-down systems for safe transport.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border">
              <img 
                src="https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/3a952776b3c8f79400ec4215bd48f3d6.png"
                alt="Ambulatory Transportation"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Ambulatory Transportation</h3>
                <p className="text-gray-600">
                  For patients who can walk but need assistance getting to and from medical appointments.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border">
              <img 
                src="https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/3cc4a8a54cafc92eb5fa2a62be37c51a.png"
                alt="Gurney Transportation"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Gurney Transportation</h3>
                <p className="text-gray-600">
                  Specialized vehicles for patients who need to remain lying down during transport.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border">
              <img 
                src="https://public.readdy.ai/ai/img_res/3e1b7a947464aaf9234e3d588523b8b4.jpg"
                alt="Hospital Discharge Transportation"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Hospital Discharge Transportation</h3>
                <p className="text-gray-600">
                  Safe, coordinated transport from hospital to home with professional assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Patients Say
            </h2>
            <p className="text-xl text-gray-600">
              Real experiences from the people we serve
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
              </div>
              <p className="text-gray-600 mb-6 italic">
                "InteCare has been a lifesaver for my weekly dialysis appointments. The drivers are always on time, professional, and so caring. I feel safe and comfortable every trip."
              </p>
              <div className="flex items-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20smiling%20elderly%20woman%2C%20warm%20expression%2C%20healthcare%20patient%2C%20friendly%20face%2C%20medical%20appointment%20patient%2C%20senior%20citizen%2C%20kind%20eyes%2C%20professional%20portrait%20photography&width=60&height=60&seq=testimonial-1&orientation=squarish"
                  alt="Margaret Thompson"
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">Margaret Thompson</h4>
                  <p className="text-gray-500 text-sm">Dialysis Patient</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
              </div>
              <p className="text-gray-600 mb-6 italic">
                "As a case manager, I rely on InteCare for my clients' transportation needs. They're reliable, professional, and always handle my clients with the utmost care and respect."
              </p>
              <div className="flex items-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20middle-aged%20woman%20case%20manager%2C%20confident%20smile%2C%20healthcare%20professional%2C%20business%20attire%2C%20medical%20social%20worker%2C%20professional%20portrait%2C%20caring%20expression&width=60&height=60&seq=testimonial-2&orientation=squarish"
                  alt="Sarah Johnson"
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">Sarah Johnson</h4>
                  <p className="text-gray-500 text-sm">Case Manager</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
              </div>
              <p className="text-gray-600 mb-6 italic">
                "After my husband's surgery, InteCare provided excellent wheelchair transport service. The driver was patient, helpful, and made sure my husband was comfortable throughout the journey."
              </p>
              <div className="flex items-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20caring%20middle-aged%20man%2C%20warm%20smile%2C%20family%20caregiver%2C%20supportive%20expression%2C%20healthcare%20family%20member%2C%20kind%20face%2C%20professional%20portrait%20photography&width=60&height=60&seq=testimonial-3&orientation=squarish"
                  alt="Robert Martinez"
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">Robert Martinez</h4>
                  <p className="text-gray-500 text-sm">Family Caregiver</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need a Ride to Your Appointment?
          </h2>
          <p className="text-xl text-blue-100 mb-2">
            Experience safe, reliable medical transportation with Integrity and Care
          </p>
          <p className="text-lg text-blue-200 mb-8">
            We are here for you 24/7
          </p>
          <div className="flex justify-center">
            <a 
              href="tel:+17147987318"
              className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 font-semibold text-lg px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (714) 798-7318
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
